import { useMemo, useState } from 'react'

import {
  AccountCircle,
  ChevronRight,
  ExpandLess,
  ExpandMore,
  Logout,
  ManageAccounts,
  ViewSidebarOutlined,
} from '@mui/icons-material'
import {
  Collapse,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom'

import ImpersonateUserModal from '~/features/Auth/components/ImpersonateUserModal'
import { canImpersonateUser } from '~/features/Auth/utils/can-impersonate-user'
import { type User } from '~/utils/types/user'

import { type MenuTab } from '../types/menu'
import { getMenuIcon } from '../utils/menu-icons'
import {
  getExternalMenuItemHref,
  getMenuItemPath,
  getMenuLabelKey,
} from '../utils/menu-utils'
import {
  NavigationItemButton,
  NavigationItemIcon,
  SidebarDrawer,
} from './SidebarMenu.styles'

interface SidebarMenuProps {
  tabs: MenuTab[]
  user: User
  mobileOpen: boolean
  onMobileClose: () => void
  onLogout: () => void
  collapsed: boolean
  onToggleCollapsed: () => void
  onCollapsedHoverChange?: (hovered: boolean) => void
}

const SIDEBAR_WIDTH = 288
const SIDEBAR_COLLAPSED_WIDTH = 76
const SUPPORT_TAB: MenuTab = {
  item: 'support',
  shouldShowOnHeader: true,
}

export { SIDEBAR_COLLAPSED_WIDTH, SIDEBAR_WIDTH }

export default function SidebarMenu({
  tabs,
  user,
  mobileOpen,
  onMobileClose,
  onLogout,
  collapsed,
  onToggleCollapsed,
  onCollapsedHoverChange,
}: SidebarMenuProps) {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>(
    {},
  )
  const [isHovered, setIsHovered] = useState(false)
  const [accountAnchorEl, setAccountAnchorEl] = useState<HTMLElement | null>(
    null,
  )
  const [isImpersonateOpened, setIsImpersonateOpened] = useState(false)
  const canImpersonate = canImpersonateUser(user, window.location.hostname)
  const isVisuallyCollapsed = collapsed && !isHovered && !accountAnchorEl

  const visibleTabs = useMemo(() => {
    const seen = new Set<string>()
    return tabs.filter(tab => {
      if (seen.has(tab.item)) return false
      seen.add(tab.item)
      return true
    })
  }, [tabs])

  function isActive(tab: MenuTab) {
    const itemPath = getMenuItemPath(tab.item, user)
    const optionIsActive = tab.options?.some(
      option => location.pathname === `/${option}`,
    )

    return (
      location.pathname === itemPath ||
      location.pathname.startsWith(`${itemPath}/`) ||
      !!optionIsActive
    )
  }

  function toggleItem(item: string) {
    setExpandedItems(previous => ({
      ...previous,
      [item]: !previous[item],
    }))
  }

  function closeMobileMenu() {
    if (!isDesktop) onMobileClose()
  }

  function closeAccountMenu() {
    setAccountAnchorEl(null)
  }

  function handleDropdownClick(item: string, isCollapsed: boolean) {
    if (isCollapsed) {
      setExpandedItems(previous => ({ ...previous, [item]: true }))
      onToggleCollapsed()
      return
    }

    toggleItem(item)
  }

  function renderTab(tab: MenuTab, isCollapsed: boolean) {
    const isDropdown = !!tab.options?.length
    const active = isActive(tab)
    const isExpanded = expandedItems[tab.item] ?? active
    const itemPath = getMenuItemPath(tab.item, user)
    const label = t(getMenuLabelKey(tab.item, user))
    const Icon = getMenuIcon(tab.item, user.role)
    const externalHref = getExternalMenuItemHref(tab.item, user)

    if (externalHref) {
      return (
        <Tooltip
          key={tab.item}
          title={isCollapsed ? label : ''}
          placement="right"
        >
          <ListItem disablePadding>
            <NavigationItemButton
              component="a"
              href={externalHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={closeMobileMenu}
              data-collapsed={isCollapsed || undefined}
            >
              <NavigationItemIcon $collapsed={isCollapsed}>
                <Icon />
              </NavigationItemIcon>
              {!isCollapsed && <ListItemText primary={label} />}
            </NavigationItemButton>
          </ListItem>
        </Tooltip>
      )
    }

    if (isDropdown) {
      return (
        <Stack key={tab.item}>
          <ListItem disablePadding>
            <Tooltip title={isCollapsed ? label : ''} placement="right">
              <NavigationItemButton
                selected={active}
                onClick={() => {
                  handleDropdownClick(tab.item, isCollapsed)
                }}
                data-collapsed={isCollapsed || undefined}
              >
                <NavigationItemIcon $collapsed={isCollapsed}>
                  <Icon />
                </NavigationItemIcon>
                {!isCollapsed && <ListItemText primary={label} />}
                {!isCollapsed && (isExpanded ? <ExpandLess /> : <ExpandMore />)}
              </NavigationItemButton>
            </Tooltip>
          </ListItem>
          <Collapse
            in={isExpanded && !isCollapsed}
            timeout="auto"
            unmountOnExit
          >
            <List disablePadding>
              {tab.options?.map(option => {
                const optionPath = `/${option}`
                const optionActive = location.pathname === optionPath
                const OptionIcon = getMenuIcon(option)
                const optionExternalHref = getExternalMenuItemHref(option, user)

                return (
                  <ListItem key={option} disablePadding>
                    {optionExternalHref ? (
                      <NavigationItemButton
                        component="a"
                        href={optionExternalHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={closeMobileMenu}
                        data-nested
                      >
                        <NavigationItemIcon $nested>
                          <OptionIcon />
                        </NavigationItemIcon>
                        <ListItemText primary={t(getMenuLabelKey(option))} />
                      </NavigationItemButton>
                    ) : (
                      <NavigationItemButton
                        component={RouterLink}
                        to={optionPath}
                        selected={optionActive}
                        onClick={closeMobileMenu}
                        data-nested
                      >
                        <NavigationItemIcon $nested>
                          <OptionIcon />
                        </NavigationItemIcon>
                        <ListItemText primary={t(getMenuLabelKey(option))} />
                      </NavigationItemButton>
                    )}
                  </ListItem>
                )
              })}
            </List>
          </Collapse>
        </Stack>
      )
    }

    return (
      <Tooltip
        key={tab.item}
        title={isCollapsed ? label : ''}
        placement="right"
      >
        <ListItem disablePadding>
          <NavigationItemButton
            component={RouterLink}
            to={itemPath}
            selected={active}
            onClick={closeMobileMenu}
            data-collapsed={isCollapsed || undefined}
          >
            <NavigationItemIcon $collapsed={isCollapsed}>
              <Icon />
            </NavigationItemIcon>
            {!isCollapsed && <ListItemText primary={label} />}
          </NavigationItemButton>
        </ListItem>
      </Tooltip>
    )
  }

  function renderContent(isCollapsed: boolean, canCollapse: boolean) {
    const pinLabel = t('common.shell.pinNav')

    return (
      <Stack
        component="nav"
        aria-label={t('views.toolbar.platform')}
        width="100%"
        minHeight="100%"
        py={1.5}
      >
        {canCollapse && (
          <Stack alignItems={isCollapsed ? 'center' : 'flex-end'} px={1} pb={1}>
            <Tooltip title={pinLabel} placement="right">
              <IconButton
                aria-label={pinLabel}
                onClick={onToggleCollapsed}
                size="small"
              >
                <ViewSidebarOutlined sx={{ transform: 'scaleX(-1)' }} />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
        <List disablePadding sx={{ flex: 1 }}>
          {visibleTabs.map(tab => renderTab(tab, isCollapsed))}
        </List>
        <List disablePadding>{renderTab(SUPPORT_TAB, isCollapsed)}</List>
        <Divider />
        <List disablePadding>
          <Tooltip title={isCollapsed ? user.name : ''} placement="right">
            <ListItem disablePadding>
              <NavigationItemButton
                data-collapsed={isCollapsed || undefined}
                onClick={event => {
                  setAccountAnchorEl(event.currentTarget)
                }}
                aria-controls={
                  accountAnchorEl ? 'sidebar-account-menu' : undefined
                }
                aria-haspopup="true"
                aria-expanded={accountAnchorEl ? 'true' : undefined}
              >
                <NavigationItemIcon $collapsed={isCollapsed}>
                  <AccountCircle />
                </NavigationItemIcon>
                {!isCollapsed && (
                  <ListItemText
                    primary={user.name}
                    secondary={t(`role.${user.role}`, {
                      defaultValue: user.role,
                    })}
                  />
                )}
                {!isCollapsed && <ChevronRight />}
              </NavigationItemButton>
            </ListItem>
          </Tooltip>
        </List>
      </Stack>
    )
  }

  return (
    <>
      <SidebarDrawer
        $width={isVisuallyCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH}
        variant="permanent"
        open
        onMouseEnter={() => {
          if (collapsed) {
            setIsHovered(true)
            onCollapsedHoverChange?.(true)
          }
        }}
        onMouseLeave={() => {
          if (collapsed) {
            setIsHovered(false)
            onCollapsedHoverChange?.(false)
          }
        }}
        sx={{ display: { xs: 'none', md: 'block' } }}
      >
        {renderContent(isVisuallyCollapsed, true)}
      </SidebarDrawer>
      <SidebarDrawer
        $width={SIDEBAR_WIDTH}
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{ display: { xs: 'block', md: 'none' } }}
      >
        {renderContent(false, false)}
      </SidebarDrawer>
      <Menu
        anchorEl={accountAnchorEl}
        id="sidebar-account-menu"
        open={Boolean(accountAnchorEl)}
        onClose={closeAccountMenu}
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        slotProps={{
          paper: {
            sx: { minWidth: 220 },
          },
        }}
      >
        <MenuItem
          onClick={() => {
            closeAccountMenu()
            closeMobileMenu()
            navigate('/profile')
          }}
        >
          <ListItemIcon>
            <AccountCircle fontSize="small" />
          </ListItemIcon>
          {t('views.toolbar.my-profile')}
        </MenuItem>
        {canImpersonate && (
          <>
            <Divider />
            <MenuItem
              onClick={() => {
                closeAccountMenu()
                closeMobileMenu()
                setIsImpersonateOpened(true)
              }}
            >
              <ListItemIcon>
                <ManageAccounts fontSize="small" />
              </ListItemIcon>
              {t('impersonate.button')}
            </MenuItem>
          </>
        )}
        <Divider />
        <MenuItem
          onClick={() => {
            closeAccountMenu()
            closeMobileMenu()
            onLogout()
          }}
        >
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          {t('views.toolbar.logout')}
        </MenuItem>
      </Menu>
      {canImpersonate && (
        <ImpersonateUserModal
          isOpened={isImpersonateOpened}
          setIsOpened={setIsImpersonateOpened}
        />
      )}
    </>
  )
}
